/**
 * Filter state that survives SPA navigation but not a reload.
 *
 * The spec wants a list to keep its filter while the user visits a detail page and comes
 * back, yet start from the default saved condition on a fresh load. React state alone is
 * lost when the route unmounts, and localStorage survives the reload it must not, so the
 * state lives in sessionStorage and is dropped once per document when that document was
 * reloaded or opened anew.
 */

const clearedKeys = new Set<string>();

/**
 * True when the current document was reloaded or freshly opened, rather than reached by
 * an in-app navigation. `back_forward` counts as navigation so the browser Back button
 * keeps the filter, matching "đi vào detail rồi back".
 */
const isFreshDocument = () => {
  const [entry] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  // Without the timing API the safe answer is "fresh": the filter resets, which is the
  // spec's fallback state rather than a stale filter the user cannot explain.
  if (!entry) return true;
  return entry.type === "reload" || entry.type === "navigate";
};

/**
 * Reads the stored state, discarding it once per document on a fresh load. Subsequent
 * reads within the same document — a remount after visiting a detail page — return it.
 */
export const readSessionFilterState = <T>(key: string): T | null => {
  if (isFreshDocument() && !clearedKeys.has(key)) {
    clearedKeys.add(key);
    sessionStorage.removeItem(key);
    return null;
  }
  const saved = sessionStorage.getItem(key);
  if (!saved) return null;
  try {
    return JSON.parse(saved) as T;
  } catch {
    return null;
  }
};

export const writeSessionFilterState = (key: string, value: unknown) => {
  // A remount must not re-clear what was just written, so the key counts as handled.
  clearedKeys.add(key);
  sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * Drops a value an earlier build persisted to localStorage. Those keys are no longer read,
 * so without this they would sit in the browser forever holding a filter the user cannot see.
 */
export const dropLegacyLocalFilterState = (keys: string[]) => {
  keys.forEach((key) => localStorage.removeItem(key));
};

export const clearSessionFilterState = (key: string) => {
  clearedKeys.add(key);
  sessionStorage.removeItem(key);
};
